import React, { useEffect } from 'react';
import { Modal, ModalMedia, ModalFooter } from '@lux/modal';
import { Delivery, GraphicSize } from '@lux/graphics';
import { Translate, useFormatters } from '@pegasus/translation';
import { ComplementaryColor, screenSizes } from '@lux/core';
import { ZalandoPlusBadge } from '@pegasus/plus';
import { Button, ButtonColor, ButtonType } from '@lux/buttons';
import { useSelector } from 'react-redux';
import { getClientDeviceWidth } from '@client/redux/modules/clientDevice/selectors';
import { trackSelectContent, trackViewContent } from '@browsing/tracking';
import { useConfig } from '@browsing/config';
import {
    ModalDescription,
    ModalTitle,
    ModalOverlay,
    ModalWrapper,
    PlusBadgeWrapper,
} from './styles';

interface EligibleFreeDeliveryDialogProps {
    open: boolean;
    onClose: () => void;
    onTurnOnPlusClick: () => void;
    minimumOrderValue: number;
}

export const EligibleFreeDeliveryDialog: React.FC<EligibleFreeDeliveryDialogProps> = ({
    open,
    onClose,
    onTurnOnPlusClick,
    minimumOrderValue = 79,
}) => {
    const deviceWidth = useSelector(getClientDeviceWidth);
    const config = useConfig();
    const isMobileView = deviceWidth < screenSizes.sm;
    const { formatPrice } = useFormatters();
    const fs_link = config.fashion_store_link;

    useEffect(() => {
        if (open) {
            trackViewContent('loyalty_free_delivery');
        }
    }, [open]);

    const clickedTermsAndConditions = () => {
        trackSelectContent('loyalty_free_delivery', 'click', {
            info: 'click',
            contentId: 'terms_and_conditions',
        });

        window.open(`${fs_link}zalando-terms-loyalty`, '_blank');
    };

    return (
        <Modal
            dismissable
            open={open}
            onOpenChange={onClose}
            purpose="NON_COMMERCIAL"
            role="dialog"
            size="S"
            sizeSm="S"
            mobileFullScreen={isMobileView}
        >
            <ModalOverlay
                dismissLabel="Close dialog"
                withoutCloseIcon
                onClick={() => {
                    trackSelectContent('loyalty_free_delivery', 'click', {
                        info: 'click',
                        contentId: 'close',
                    });
                    onClose();
                }}
            >
                <ModalWrapper onClick={e => e.stopPropagation()} isMember={false}>
                    <PlusBadgeWrapper>
                        <ZalandoPlusBadge />
                    </PlusBadgeWrapper>

                    <ModalMedia>
                        <Delivery size={GraphicSize.L} bgColor={ComplementaryColor.brand30} />
                    </ModalMedia>

                    <ModalTitle>
                        <Translate
                            message="free_delivery.eligible.popup.title"
                            params={{
                                minimumOrderValue: formatPrice({
                                    cents: minimumOrderValue * 100,
                                    maximumFractionDigits: 0,
                                }),
                            }}
                        />
                    </ModalTitle>

                    <ModalDescription>
                        <Translate message="free_delivery.eligible.popup.description" />
                        <a onClick={clickedTermsAndConditions}>
                            {' '}
                            <Translate message="free_delivery.eligible.popup.description_tnc" />
                        </a>
                    </ModalDescription>

                    <ModalFooter
                        primaryAction={{
                            children: (
                                <Translate message="free_delivery.eligible.popup.turn_on_plus" />
                            ),
                            href: '#',
                            link: true,
                            onClick: () => {
                                trackSelectContent('loyalty_free_delivery', 'click', {
                                    info: 'click',
                                    contentId: 'turn_on_plus',
                                });
                                onTurnOnPlusClick();
                            },
                        }}
                    >
                        <Button
                            onClick={() => {
                                trackSelectContent('loyalty_free_delivery', 'click', {
                                    info: 'click',
                                    contentId: 'postpone',
                                });
                                onClose();
                            }}
                            buttonType={ButtonType.tertiary}
                            color={ButtonColor.dark}
                        >
                            <Translate message="free_delivery.eligible.popup.maybeLater" />
                        </Button>
                    </ModalFooter>
                </ModalWrapper>
            </ModalOverlay>
        </Modal>
    );
};
